import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import './SportSelector.css';

interface SportSelectorProps {
  variant?: 'default' | 'header';
}

const SportSelector: React.FC<SportSelectorProps> = ({ variant = 'default' }) => {
  const { currentSport, setSport, theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const sports = [
    { id: 'pickleball', name: 'Pickleball', icon: '🏓', useLogo: true, logoFile: 'mypickleballcoach.online.png' },
    { id: 'tennis', name: 'Tennis', icon: '🎾', useLogo: true, logoFile: 'mytennisgamecoach.online.png' },
    { id: 'golf', name: 'Golf', icon: '⛳', useLogo: true, logoFile: 'mygolfgamecoach.online.png' },
    { id: 'hiking', name: 'Hiking', icon: '🥾', useLogo: true, logoFile: 'myhikingcoach.online.png' },
    { id: 'padel', name: 'Padel', icon: '🏓', useLogo: true, logoFile: 'padel-icon.svg' },
    { id: 'badminton', name: 'Badminton', icon: '🏸', useLogo: true, logoFile: 'badminton-icon.svg' },
    { id: 'racquetball', name: 'Racquetball', icon: '🏓', useLogo: true, logoFile: 'racquetball-icon.svg' },
    { id: 'squash', name: 'Squash', icon: '🏓', useLogo: true, logoFile: 'squash-icon.svg' },
    { id: 'running', name: 'Running', icon: '🏃', useLogo: false },
    { id: 'cycling', name: 'Cycling', icon: '🚴', useLogo: false },
    { id: 'swimming', name: 'Swimming', icon: '🏊', useLogo: false },
    { id: 'strength', name: 'Strength', icon: '💪', useLogo: false }
  ];

  const handleSportChange = (sportId: string) => {
    setSport(sportId);
    setIsOpen(false);
    const path = location.pathname;
    // Recovery-rehab: update URL for new sport
    const recoveryMatch = path.match(/^(\/recovery-rehab)(?:\/([^/]+))?/);
    if (recoveryMatch) {
      navigate(`/recovery-rehab/${sportId}`, { replace: true });
      return;
    }
    // Sport-specific pages: redirect when new sport doesn't have this feature
    if (path === '/trail-prep' && sportId !== 'hiking') {
      navigate('/', { replace: true });
      return;
    }
    if (path === '/find-players' && sportId !== 'pickleball') {
      navigate('/', { replace: true });
      return;
    }
  };

  return (
    <div className={`sport-selector ${variant === 'header' ? 'sport-selector-header' : ''}`}>
      <button 
        className="sport-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        style={variant === 'header' ? undefined : { 
          backgroundColor: theme.colors.surface,
          color: theme.colors.text,
          borderColor: theme.colors.border
        }}
      >
        <span className="current-sport-icon">
          {currentSport.id === 'pickleball' ? (
            <img 
              src="/pickleball-icon.svg" 
              alt="Pickleball Logo" 
              className="sport-logo-small"
            />
          ) : currentSport.id === 'golf' ? (
            <span className="sport-emoji">⛳</span>
          ) : currentSport.id === 'tennis' ? (
            <img 
              src="/tennis-icon.svg" 
              alt="Tennis Logo" 
              className="sport-logo-small"
            />
          ) : currentSport.id === 'hiking' ? (
            <span className="sport-emoji">🥾</span>
          ) : currentSport.id === 'padel' ? (
            <img 
              src="/padel-icon.svg" 
              alt="Padel Logo" 
              className="sport-logo-small"
            />
          ) : currentSport.id === 'badminton' ? (
            <img 
              src="/badminton-icon.svg" 
              alt="Badminton Logo" 
              className="sport-logo-small"
            />
          ) : currentSport.id === 'racquetball' ? (
            <img 
              src="/racquetball-icon.svg" 
              alt="Racquetball Logo" 
              className="sport-logo-small"
            />
          ) : currentSport.id === 'squash' ? (
            <img 
              src="/squash-icon.svg" 
              alt="Squash Logo" 
              className="sport-logo-small"
            />
          ) : currentSport.id === 'running' ? (
            <span className="sport-emoji">🏃</span>
          ) : currentSport.id === 'cycling' ? (
            <span className="sport-emoji">🚴</span>
          ) : currentSport.id === 'swimming' ? (
            <span className="sport-emoji">🏊</span>
          ) : currentSport.id === 'strength' ? (
            <span className="sport-emoji">💪</span>
          ) : (
            <span className="sport-emoji">🎾</span>
          )}
        </span>
        <span className="current-sport-name">{currentSport.name}</span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      
      {isOpen && (
        <div 
          className="sport-dropdown"
          style={variant === 'header' ? undefined : { 
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.border
          }}
        >
          {sports.map((sport) => (
            <button
              key={sport.id}
              className={`sport-option ${sport.id === currentSport.id ? 'active' : ''}`}
              onClick={() => handleSportChange(sport.id)}
              style={{
                backgroundColor: sport.id === currentSport.id ? 'var(--color-sport-accent)' : 'transparent',
                color: sport.id === currentSport.id ? 'white' : theme.colors.text
              }}
            >
              <span className="sport-icon">
                {sport.id === 'pickleball' ? (
                  <img 
                    src="/pickleball-icon.svg" 
                    alt="Pickleball Logo" 
                    className="sport-logo-small"
                  />
                ) : sport.id === 'golf' ? (
                  <span className="sport-emoji">⛳</span>
                ) : sport.id === 'tennis' ? (
                  <img 
                    src="/tennis-icon.svg" 
                    alt="Tennis Logo" 
                    className="sport-logo-small"
                  />
                ) : sport.id === 'hiking' ? (
                  <span className="sport-emoji">🥾</span>
                ) : sport.id === 'padel' ? (
                  <img 
                    src="/padel-icon.svg" 
                    alt="Padel Logo" 
                    className="sport-logo-small"
                  />
                ) : sport.id === 'badminton' ? (
                  <img 
                    src="/badminton-icon.svg" 
                    alt="Badminton Logo" 
                    className="sport-logo-small"
                  />
                ) : sport.id === 'racquetball' ? (
                  <img 
                    src="/racquetball-icon.svg" 
                    alt="Racquetball Logo" 
                    className="sport-logo-small"
                  />
                ) : sport.id === 'squash' ? (
                  <img 
                    src="/squash-icon.svg" 
                    alt="Squash Logo" 
                    className="sport-logo-small"
                  />
                ) : sport.id === 'running' ? (
                  <span className="sport-emoji">🏃</span>
                ) : sport.id === 'cycling' ? (
                  <span className="sport-emoji">🚴</span>
                ) : sport.id === 'swimming' ? (
                  <span className="sport-emoji">🏊</span>
                ) : sport.id === 'strength' ? (
                  <span className="sport-emoji">💪</span>
                ) : (
                  <span className="sport-emoji">🎾</span>
                )}
              </span>
              <span className="sport-name">{sport.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SportSelector;